<template>
	<div class="company-records-list">
		<div class="controls-row">
			<section class="filters-bar" aria-label="Company record filters">
				<div class="filters-bar-start">
					<AppointmentIcon name="filter" class="filter-bar-icon" />
				</div>
				<div class="category-filter-group" role="group" aria-label="Show">
					<button
						v-for="view in views"
						:key="view.value"
						type="button"
						class="category-filter-btn"
						:class="{ active: activeView === view.value }"
						:aria-pressed="activeView === view.value"
						@click="selectView(view.value)"
					>
						{{ view.label }}
					</button>
				</div>
			</section>
		</div>

		<div class="table-wrapper">
			<div v-if="showInitialLoading" class="loading-state">Loading company records...</div>
			<div v-else-if="store.unavailable" class="info-state">
				Company records will be available after the next server update.
			</div>
			<div v-else-if="store.error && !store.items.length" class="error-state">
				{{ store.error }}
				<button class="link-btn" type="button" @click="refresh">Retry</button>
			</div>
			<div v-else class="records-table-container">
				<table class="records-table">
					<thead>
						<tr v-if="activeView === 'current'">
							<th class="col-record">Record</th>
							<th class="col-reference">Reference</th>
							<th class="col-version">Version</th>
							<th class="col-month">Reviewed</th>
							<th class="col-month">Next review</th>
							<th class="col-status">Status</th>
						</tr>
						<tr v-else>
							<th class="col-record">Record</th>
							<th class="col-reference">Reference</th>
							<th class="col-version">Version</th>
							<th class="col-month">Reviewed</th>
							<th class="col-signoff">Review completed</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!visibleRows.length">
							<td :colspan="activeView === 'current' ? 6 : 5" class="empty-cell">
								{{ activeView === 'current' ? 'No company records yet.' : 'No archived versions yet. A version moves here when the next yearly review starts.' }}
							</td>
						</tr>
						<tr
							v-for="row in visibleRows"
							:key="row.key"
							class="table-row"
							:class="{ active: activeRowKey === row.key }"
							@click="toggleSubmenu(row, $event)"
							@contextmenu.prevent="openSubmenu(row, $event)"
						>
							<td class="col-record" v-tooltip="() => row.title">{{ row.title }}</td>
							<td class="col-reference">{{ row.reference }}</td>
							<td class="col-version">{{ row.record ? formatRecordVersion(row.record.version) : '—' }}</td>
							<td class="col-month">{{ row.record ? formatRecordMonth(row.record.reviewedOn) || '—' : '—' }}</td>
							<template v-if="activeView === 'current'">
								<td class="col-month">{{ row.record ? formatRecordMonth(row.record.nextReviewOn) || '—' : '—' }}</td>
								<td class="col-status">
									<span class="status-badges">
										<span
											v-for="badge in row.badges"
											:key="badge.label"
											class="badge"
											:class="`badge-${badge.tone}`"
										>{{ badge.label }}</span>
									</span>
								</td>
							</template>
							<td v-else class="col-signoff" v-tooltip="() => row.signOff">{{ row.signOff || '—' }}</td>
						</tr>
					</tbody>
				</table>

				<div
					v-if="activeRow"
					ref="submenuElement"
					class="submenu"
					:style="submenuStyle"
					role="menu"
					v-click-outside="closeSubmenu"
				>
					<template v-if="activeRow.record">
						<button class="submenu-btn" type="button" role="menuitem" @click.stop="openRow(activeRow)">
							{{ activeRow.isCurrent ? 'Open record' : 'Open archived version' }}
						</button>
						<button class="submenu-btn" type="button" role="menuitem" @click.stop="previewRow(activeRow)">
							Preview
						</button>
						<button class="submenu-btn" type="button" role="menuitem" @click.stop="downloadRow(activeRow)">
							Download PDF
						</button>
						<template v-if="activeRow.isCurrent">
							<button class="submenu-btn primary" type="button" role="menuitem" @click.stop="askStartNextReview(activeRow)">
								Start next review
							</button>
							<button class="submenu-btn delete" type="button" role="menuitem" @click.stop="askDelete(activeRow)">
								Delete {{ formatRecordVersion(activeRow.record.version) }}
							</button>
						</template>
					</template>
					<button
						v-else
						class="submenu-btn primary"
						type="button"
						role="menuitem"
						:disabled="busy"
						@click.stop="startFirstVersion(activeRow)"
					>
						{{ busy ? 'Starting...' : 'Start this record' }}
					</button>
				</div>
			</div>
		</div>

		<CompanyRecordDialog
			v-if="confirmation"
			:dialog="confirmation"
			:busy="busy"
			@cancel="confirmation = null"
			@confirm="runConfirmation"
		/>

		<InvoicePreview
			:show-preview="Boolean(preview)"
			:invoice-html="preview ? preview.html : ''"
			render-html-as-pdf
			:pdf-filename="preview ? preview.filename : 'company-record.pdf'"
			@close-preview="preview = null"
			@download-pdf="downloadPreview"
		/>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import CompanyRecordDialog from '@/components/company-records/CompanyRecordDialog.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import vTooltip from '@/directives/tooltip';
import { companyRecordErrorCode, useCompanyRecordsStore } from '@/stores/companyRecords';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import {
	COMPANY_RECORD_TEMPLATE_KEYS,
	createFirstVersion,
	createNextVersion,
	formatRecordDate,
	formatRecordMonth,
	formatRecordVersion,
	getCompanyRecordTemplate,
	groupRecordVersions,
	nextReviewDialog,
	summarizeRecord,
} from '@/utils/companyRecords';
import { deriveCompanyRecordFilename, generateCompanyRecordHtml } from '@/utils/companyRecordDocument';
import { downloadProposalPdfFromHtml } from '@/utils/proposalDocument';

const emit = defineEmits(['open-record', 'record-deleted']);

const store = useCompanyRecordsStore();
const { proxy } = getCurrentInstance() || {};
const toast = proxy?.$toast;
const logError = proxy?.$logError;

const views = [
	{ value: 'current', label: 'Current' },
	{ value: 'archive', label: 'Archive' },
];
const activeView = ref('current');
const activeRowKey = ref(null);
const submenuElement = ref(null);
const submenuPosition = reactive({ top: 0, left: 0 });
const confirmation = ref(null);
const busy = ref(false);
const preview = ref(null);

const showInitialLoading = computed(() => store.isLoading && !store.hasFetched);
const groups = computed(() => groupRecordVersions(store.items));

const statusBadges = (record) => {
	const summary = summarizeRecord(record);
	const badges = [];
	if (summary.reviewDue) badges.push({ tone: 'danger', label: 'Review due' });
	if (summary.overdue) badges.push({ tone: 'danger', label: `${summary.overdue} overdue` });
	if (summary.soon) badges.push({ tone: 'warning', label: `${summary.soon} due soon` });
	if (!badges.length) badges.push({ tone: 'success', label: 'Up to date' });
	return badges;
};

const signOffLabel = (record) => {
	const { completedBy, date } = record.content.signOff;
	return [completedBy, formatRecordDate(date)].filter(Boolean).join(', ');
};

const recordTitle = (record, template) =>
	[record?.title || template?.title || 'Company record', record?.subject?.name || record?.subjectName]
		.filter(Boolean)
		.join(' – ');

const currentRows = computed(() => {
	const rows = groups.value.map((group) => {
		const record = group.versions[0];
		const template = getCompanyRecordTemplate(group.templateKey);
		return {
			key: `current:${group.key}`,
			title: recordTitle(record, template),
			reference: record.reference || template?.reference || '',
			record,
			history: group.versions.slice(1),
			isCurrent: true,
			badges: statusBadges(record),
		};
	});
	// Records nobody has started yet are listed so they can be started.
	COMPANY_RECORD_TEMPLATE_KEYS.forEach((templateKey) => {
		if (groups.value.some((group) => group.templateKey === templateKey)) return;
		const template = getCompanyRecordTemplate(templateKey);
		rows.push({
			key: `new:${templateKey}`,
			templateKey,
			title: template.title,
			reference: template.reference,
			record: null,
			history: [],
			isCurrent: true,
			badges: [{ tone: 'muted', label: 'Not started' }],
		});
	});
	return rows;
});

const archiveRows = computed(() =>
	groups.value.flatMap((group) =>
		group.versions.slice(1).map((record, index) => ({
			key: `archive:${record.documentId}`,
			title: recordTitle(record, getCompanyRecordTemplate(record.templateKey)),
			reference: record.reference,
			record,
			history: group.versions.slice(index + 2),
			isCurrent: false,
			signOff: signOffLabel(record),
		})),
	),
);

const visibleRows = computed(() => (activeView.value === 'current' ? currentRows.value : archiveRows.value));
const activeRow = computed(() => visibleRows.value.find((row) => row.key === activeRowKey.value) || null);
const submenuStyle = computed(() => ({ top: `${submenuPosition.top}px`, left: `${submenuPosition.left}px` }));

const vClickOutside = {
	mounted(el, binding) {
		el.clickOutsideEvent = (event) => {
			if (!(el === event.target || el.contains(event.target))) binding.value(event);
		};
		document.body.addEventListener('click', el.clickOutsideEvent);
	},
	unmounted(el) {
		document.body.removeEventListener('click', el.clickOutsideEvent);
	},
};

const selectView = (value) => {
	activeView.value = value;
	closeSubmenu();
};

const closeSubmenu = () => {
	activeRowKey.value = null;
};

const placeSubmenu = (event) => {
	const rect = submenuElement.value?.getBoundingClientRect?.();
	const width = rect?.width || 200;
	const height = rect?.height || 200;
	const padding = 12;
	const left = Math.min(event?.clientX || 0, window.innerWidth - width - padding);
	const top = Math.min(event?.clientY || 0, window.innerHeight - height - padding);
	submenuPosition.left = Math.max(padding, left);
	submenuPosition.top = Math.max(padding, top);
};

const openSubmenu = (row, event) => {
	event?.stopPropagation?.();
	activeRowKey.value = row.key;
	nextTick(() => placeSubmenu(event));
};

const toggleSubmenu = (row, event) => {
	event?.stopPropagation?.();
	if (activeRowKey.value === row.key) {
		closeSubmenu();
		return;
	}
	openSubmenu(row, event);
};

const refresh = async () => {
	try {
		await store.fetchRecords({ force: true });
	} catch (error) {
		logError?.(error, 'CompanyRecordsListView refresh');
	}
};

const openRow = (row) => {
	closeSubmenu();
	emit('open-record', { record: row.record });
};

const buildPdf = (row) => ({
	html: generateCompanyRecordHtml({ record: row.record, history: row.history }),
	filename: deriveCompanyRecordFilename(row.record),
});

const previewRow = (row) => {
	closeSubmenu();
	preview.value = buildPdf(row);
};

const downloadPdf = async ({ html, filename }) => {
	try {
		await downloadProposalPdfFromHtml({ html, filename, logError });
	} catch (error) {
		logError?.(error, 'CompanyRecordsListView downloadPdf');
		toast?.error?.('Failed to download PDF.');
	}
};

const downloadRow = (row) => {
	closeSubmenu();
	void downloadPdf(buildPdf(row));
};

const downloadPreview = () => {
	if (preview.value) void downloadPdf(preview.value);
};

const failureMessage = (error, fallback) => {
	if (companyRecordErrorCode(error)) return error.response.data.error.message;
	return fallback;
};

const startFirstVersion = async (row) => {
	if (busy.value) return;
	busy.value = true;
	try {
		const record = await store.createRecord(createFirstVersion(row.templateKey));
		closeSubmenu();
		emit('open-record', { record });
	} catch (error) {
		logError?.(error, 'CompanyRecordsListView startFirstVersion');
		toast?.error?.(failureMessage(error, 'Failed to start the record.'));
		await refresh();
	} finally {
		busy.value = false;
	}
};

const askStartNextReview = (row) => {
	closeSubmenu();
	confirmation.value = {
		...nextReviewDialog(row.record, row.title),
		run: async () => {
			const record = await store.createRecord(createNextVersion(row.record));
			emit('open-record', { record });
		},
		failure: 'Failed to start the next review.',
	};
};

const askDelete = (row) => {
	closeSubmenu();
	const version = formatRecordVersion(row.record.version);
	const previous = row.history[0];
	confirmation.value = {
		title: `Delete ${version} of ${row.title}`,
		lines: previous
			? [`${formatRecordVersion(previous.version)} becomes the current version again and can be changed.`]
			: ['Everything entered in this record will be lost. It can be started again from the original checklist.'],
		actionLabel: 'Delete',
		busyLabel: 'Deleting...',
		tone: 'danger',
		run: async () => {
			await store.deleteRecord(row.record.documentId);
			emit('record-deleted', { documentId: row.record.documentId });
			toast?.success?.(`${version} deleted.`);
		},
		failure: 'Failed to delete the version.',
	};
};

const runConfirmation = async () => {
	const action = confirmation.value;
	if (!action || busy.value) return;
	busy.value = true;
	try {
		await action.run();
		confirmation.value = null;
	} catch (error) {
		logError?.(error, 'CompanyRecordsListView confirmation');
		toast?.error?.(failureMessage(error, action.failure));
		confirmation.value = null;
		await refresh();
	} finally {
		busy.value = false;
	}
};

onMounted(() => {
	void store.fetchRecords().catch((error) => logError?.(error, 'CompanyRecordsListView mounted fetch'));
});

// Records changed on another device show up here without reloading, while nothing on the list is open.
const isListBusy = () =>
	!store.hasFetched ||
	store.isLoading ||
	busy.value ||
	Boolean(activeRowKey.value) ||
	Boolean(confirmation.value) ||
	Boolean(preview.value);

const refreshQuietly = async ({ canApply }) => {
	if (isListBusy()) return;
	// Until the server knows company records it keeps being asked, so the section appears once it does.
	if (store.unavailable) {
		await store.fetchRecords({ force: true });
		return;
	}
	const since = store.changeCount;
	const items = await store.peekRecords();
	if (!canApply() || isListBusy()) return;
	if (sameRecords(store.items, items)) return;
	store.applyRecords(items, { since });
};

useLiveRefresh(refreshQuietly, { isBusy: isListBusy });
</script>

<style scoped>
.company-records-list {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 16px;
	color: var(--text-color);
}

.filters-bar {
	flex: 0 1 auto;
}

.table-wrapper {
	position: relative;
	min-width: 0;
}

.records-table-container {
	width: 100%;
	min-width: 0;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

.records-table {
	width: 100%;
	min-width: 760px;
	box-sizing: border-box;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	border-inline: 1px solid var(--border-color);
	font: 400 0.875rem/1.25rem var(--font-family-base);
}

.records-table th,
.records-table td {
	box-sizing: border-box;
	padding: 8px 16px;
	border-bottom: 1px solid var(--border-color);
	text-align: left;
	vertical-align: middle;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.records-table td {
	height: 40px;
}

.records-table tbody {
	background: var(--noc-background);
}

.records-table tbody tr:not(:last-child) td:first-child {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.records-table th {
	height: 38px;
	position: sticky;
	top: 0;
	z-index: 1;
	border-top: 1px solid var(--border-color);
	background: #fafafa;
	color: var(--text-color);
	font: 600 0.8125rem/1.125rem var(--font-family-base);
}

.table-row {
	touch-action: manipulation;
	cursor: pointer;
	transition: background-color 0.15s;
}

.table-row.active {
	background: var(--noc-blue-05);
}

.col-record {
	width: 30%;
	font-weight: 600;
}

.col-reference {
	width: 13%;
}

.col-version {
	width: 9%;
}

.col-month {
	width: 14%;
}

.col-status {
	width: 20%;
}

.col-signoff {
	width: 34%;
}

.status-badges {
	display: inline-flex;
	gap: 6px;
	max-width: 100%;
	overflow: hidden;
}

.badge {
	display: inline-flex;
	align-items: center;
	min-height: 22px;
	padding: 2px 8px;
	border-radius: 4px;
	font: 600 0.75rem/1rem var(--font-family-base);
	white-space: nowrap;
}

.badge-success {
	background: var(--report-status-success-background);
	color: var(--report-status-success-color);
}

.badge-warning {
	background: var(--report-status-warning-background);
	color: var(--report-status-warning-color);
}

.badge-danger {
	background: var(--report-status-danger-background);
	color: var(--report-status-danger-color);
}

.badge-muted {
	background: var(--report-status-muted-background);
	color: var(--report-status-muted-color);
}

.empty-cell,
.loading-state,
.info-state,
.error-state {
	padding: 28px;
	text-align: center;
	color: var(--text-color-secondary);
	white-space: normal;
}

.error-state {
	color: var(--noc-red);
}

.link-btn {
	height: 34px;
	padding: 0 12px;
	border: 0;
	background: transparent;
	color: var(--noc-blue);
	font: inherit;
	cursor: pointer;
}

.submenu {
	position: fixed;
	z-index: 1000;
	width: max-content;
	max-width: calc(100vw - 24px);
	padding: 6px 0;
	border: 1px solid var(--border-color);
	border-radius: 4px;
	background-color: var(--noc-background);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 10px 16px;
	border: none;
	background: none;
	color: var(--text-color);
	text-align: left;
	font: 500 0.875rem/1.25rem var(--font-family-base);
	white-space: nowrap;
	cursor: pointer;
	touch-action: manipulation;
}

.submenu-btn.primary {
	color: var(--noc-blue);
}

.submenu-btn.delete {
	color: #dc2626;
}

.submenu-btn:disabled {
	color: var(--text-color-secondary);
	cursor: wait;
}

@media (hover: hover) {
	.table-row:hover {
		background: var(--noc-blue-05);
	}

	.submenu-btn:hover:not(:disabled) {
		background-color: var(--noc-blue-10);
		color: var(--noc-blue);
	}

	.submenu-btn.delete:hover {
		background-color: #fee2e2;
		color: #dc2626;
	}
}
</style>

<style scoped src="@/assets/styles/document-list-controls.css"></style>
